import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, formatINR, formatDate } from "@/lib/api";
import { LOAN_TYPES, calcEmi, slug } from "@/lib/constants";
import { useRates } from "@/context/RatesContext";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Home, Building2, Briefcase, Loader2, Percent } from "lucide-react";

const ICONS = { "Home Loan": Home, LAP: Building2, "Business Loan": Briefcase };

export default function AdminSettings() {
  const { rates, meta, refresh } = useRates();
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(Object.fromEntries(LOAN_TYPES.map((t) => [t, String(rates[t])]))); }, [rates]);

  const dirty = LOAN_TYPES.some((t) => Number(draft[t]) !== rates[t]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(LOAN_TYPES.map((t) => [t, Number(draft[t])]));
      await api.put("/settings/rates", { rates: payload });
      await refresh();
      toast.success("Rate cards updated — calculator, wizard and sanction letters now use the new defaults");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B132B] text-white">
      <Navbar />
      <main className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-12 py-10 lg:py-16" data-testid="admin-settings-page">
        <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#FF6B00]">Falcon Operations · Settings</p>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold">Rate cards</h1>
        <p className="mt-2 text-slate-400">Indicative starting rates across our partner banks and NBFCs, per product. Clients can fine-tune the rate in the calculator; admins record the lender's actual sanctioned rate per file.</p>
        {meta.updated_at && <p className="mt-1 text-xs text-slate-500" data-testid="rates-last-updated">Last updated {formatDate(meta.updated_at)} by {meta.updated_by}</p>}

        <div className="mt-8 grid md:grid-cols-3 gap-6">
          {LOAN_TYPES.map((t) => {
            const Icon = ICONS[t];
            const v = Number(draft[t]);
            const valid = v >= 5 && v <= 24;
            return (
              <div key={t} className="bg-white text-[#0F172A] rounded-2xl p-6 shadow-xl" data-testid={`rate-card-${slug(t)}`}>
                <Icon className="h-7 w-7 text-[#FF6B00]" />
                <h3 className="mt-4 font-display text-lg font-semibold">{t}</h3>
                <Label htmlFor={`rate-${slug(t)}`} className="mt-4 block text-xs uppercase tracking-wider text-slate-500">Default rate (% p.a.)</Label>
                <div className="relative mt-1.5">
                  <Input id={`rate-${slug(t)}`} type="number" min="5" max="24" step="0.05" value={draft[t] ?? ""} onChange={(e) => setDraft({ ...draft, [t]: e.target.value })} className={`h-12 pr-9 text-lg font-semibold ${valid ? "" : "border-red-400"}`} data-testid={`rate-input-${slug(t)}`} />
                  <Percent className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                </div>
                <p className="mt-3 text-xs text-slate-500">₹1 Cr over 20 yrs → <strong className="text-slate-700">{valid ? formatINR(calcEmi(10000000, v, 20)) : "—"}</strong>/mo</p>
                {Number(draft[t]) !== rates[t] && <p className="mt-1 text-xs text-[#E05E00]" data-testid={`rate-current-${slug(t)}`}>Currently {rates[t].toFixed(2)}%</p>}
              </div>
            );
          })}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-4">
          <Button onClick={save} disabled={!dirty || saving || LOAN_TYPES.some((t) => !(Number(draft[t]) >= 5 && Number(draft[t]) <= 24))} className="gold-btn rounded-full h-11 px-8" data-testid="save-rates-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish rate cards"}
          </Button>
          {dirty && <Button variant="ghost" className="text-slate-300 hover:bg-white/10 hover:text-white" onClick={() => setDraft(Object.fromEntries(LOAN_TYPES.map((t) => [t, String(rates[t])])))} data-testid="reset-rates-button">Discard changes</Button>}
        </div>
      </main>
    </div>
  );
}
